import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import StatusChip from '../../components/ui/StatusChip';

const QA_BADGE = {
  pending:  'bg-amber-100 text-amber-700 border border-amber-200',
  approved: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border border-red-200',
};

const QUALITY_RESULT_BADGE = {
  Passed: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  'Partially Passed': 'bg-amber-100 text-amber-700 border border-amber-200',
  Rejected: 'bg-red-100 text-red-700 border border-red-200',
};

// Derived label — no new stored status; computed from the existing qaStatus + item quantities.
const qualityResult = (g) => {
  if (!g || g.qaStatus === 'pending') return null;
  if (g.qaStatus === 'rejected') return 'Rejected';
  const hasIssue = (g.items || []).some((it) => (it.damagedQty || 0) > 0 || (it.missingQty || 0) > 0);
  return hasIssue ? 'Partially Passed' : 'Passed';
};

const GRNList = () => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/grn')
      .then((r) => setGrns(r.data.data))
      .catch(() => toast.error('Failed to load GRNs'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = grns.filter((g) =>
    g.grnNumber?.toLowerCase().includes(search.toLowerCase()) ||
    g.inwardNumber?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <input
          type="text"
          className="zip-input max-w-xs"
          placeholder="Search GRN or Inward No…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {can('grn.create') && (
          <button
            onClick={() => navigate('/grn/new')}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={14} /> New GRN
          </button>
        )}
      </div>

      <div className="zip-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zip-muted text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-zip-muted text-sm">No GRNs found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-zip-surface border-b border-zip-border">
                  {['GRN No.', 'Inward No.', 'Client', 'Items', 'Status', 'QA Status', 'Quality Result', 'Date'].map((h) => (
                    <th key={h} className="zip-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((g) => (
                  <tr
                    key={g._id}
                    onClick={() => navigate(`/grn/${g._id}`)}
                    className="border-b border-zip-border last:border-0 hover:bg-zip-hover cursor-pointer transition-colors"
                  >
                    <td className="zip-td font-mono text-xs text-zip-red">{g.grnNumber}</td>
                    <td className="zip-td font-mono text-xs">{g.inwardNumber}</td>
                    <td className="zip-td">{g.client?.name}</td>
                    <td className="zip-td font-mono">{g.items?.length ?? 0}</td>
                    <td className="zip-td"><StatusChip status={g.status} /></td>
                    <td className="zip-td">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${QA_BADGE[g.qaStatus]}`}>
                        {g.qaStatus}
                      </span>
                    </td>
                    <td className="zip-td">
                      {qualityResult(g) && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${QUALITY_RESULT_BADGE[qualityResult(g)]}`}>
                          {qualityResult(g)}
                        </span>
                      )}
                    </td>
                    <td className="zip-td text-zip-muted text-xs">
                      {format(new Date(g.createdAt), 'dd MMM yyyy')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GRNList;
